import { lazy, Suspense, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { Loader2, Pencil, Pin, PinOff, Send } from "lucide-react";
import "@uiw/react-md-editor/markdown-editor.css";

// Lazy-loaded so the Markdown editor + its dependencies stay out of the
// initial job-detail chunk and only load when the office notes panel is used.
const MDEditor = lazy(() => import("@uiw/react-md-editor"));
const MDMarkdown = lazy(() => import("@uiw/react-md-editor").then((m) => ({ default: m.default.Markdown })));

export interface JobNote {
    id: string;
    job_id: string;
    author_user_id: string | null;
    author_name: string | null;
    body: string;
    pinned: boolean;
    created_at: string;
    updated_at: string;
}

// Author edits are allowed for 5 minutes after posting (server-enforced;
// this only decides whether to SHOW the edit button).
const EDIT_WINDOW_MS = 5 * 60 * 1000;

/**
 * Pin/unpin + the author's 5-minute typo edit for ONE note.
 *
 * These used to live on a note row inside this panel's own thread list. The
 * thread now renders as rows in the Activity feed (#1377 — the panel-local
 * list was mounted only while the composer was open, so every note on the
 * job page vanished the moment you closed it), and the feed row mounts this
 * for its actions.
 */
export function JobNoteActions({
    jobId,
    noteId,
    body,
    pinned,
    authorUserId,
    createdAt,
    currentUserId,
    onChanged,
}: {
    jobId: string;
    noteId: string;
    body: string;
    pinned: boolean;
    authorUserId: string | null;
    createdAt: string;
    currentUserId: string | null;
    onChanged: () => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(body);

    const patch = useMutation({
        mutationFn: (vars: { body?: string; pinned?: boolean }) =>
            api.patch(`/api/jobs/${jobId}/notes/${noteId}`, vars),
        onSuccess: () => {
            setEditing(false);
            onChanged();
        },
        onError: (e: unknown) => {
            const err = (e as { body?: { message?: string } } | null)?.body;
            toast.error(err?.message ?? "Couldn't update the note");
        },
    });

    const canEdit =
        authorUserId != null &&
        authorUserId === currentUserId &&
        Date.now() - new Date(createdAt).getTime() < EDIT_WINDOW_MS;

    if (editing) {
        return (
            <div className="mt-1.5 space-y-1.5">
                <Textarea
                    rows={3}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="text-sm"
                    autoFocus
                />
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        className="h-7 text-xs"
                        disabled={patch.isPending || !draft.trim()}
                        onClick={() => patch.mutate({ body: draft.trim() })}
                    >
                        {patch.isPending ? "Saving…" : "Save"}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(false)}>
                        Cancel
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-1 flex items-center gap-1.5">
            <Button
                size="sm"
                variant="ghost"
                className="h-6 gap-1 px-1.5 text-[10px] text-foreground/60"
                title={pinned ? "Unpin" : "Pin to the top of the chat"}
                disabled={patch.isPending}
                onClick={() => patch.mutate({ pinned: !pinned })}
            >
                {pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                {pinned ? "Unpin" : "Pin to top"}
            </Button>
            {canEdit && (
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 gap-1 px-1.5 text-[10px] text-foreground/60"
                    title="Edit (5-minute window)"
                    onClick={() => {
                        setDraft(body);
                        setEditing(true);
                    }}
                >
                    <Pencil className="h-3 w-3" />
                    Edit
                </Button>
            )}
        </div>
    );
}

/**
 * Job Notes composer — append-only (JOB NOTES — MULTI-NOTE THREAD). Every
 * add CREATES a note; nothing overwrites. The notes themselves render as
 * rows in the Activity feed, so they survive this composer being closed
 * (#1377); this panel is the typing surface only.
 *
 * The office Markdown notes (jobs.notes_rich) keep their own editor below —
 * they're an office-only formatted scratchpad, not part of the thread.
 */
export function JobNotesPanel({ jobId, notesRich }: { jobId: string; notesRich: string | null }) {
    const qc = useQueryClient();
    const [draft, setDraft] = useState("");

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["job-notes", jobId] });
        qc.invalidateQueries({ queryKey: ["job", jobId] });
        qc.invalidateQueries({ queryKey: ["activity-timeline"] });
    };

    const addNote = useMutation({
        mutationFn: (body: string) => api.post(`/api/jobs/${jobId}/notes`, { body }),
        onSuccess: () => {
            setDraft("");
            invalidate();
            toast.success("Note added");
        },
        onError: () => toast.error("Couldn't add the note"),
    });

    // Office (formatted, Markdown) notes — independent edit state + mutation.
    const [editingRich, setEditingRich] = useState(false);
    const [richDraft, setRichDraft] = useState(notesRich ?? "");
    const colorMode =
        typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light";

    const saveRich = useMutation({
        mutationFn: (value: string | null) => api.patch(`/api/jobs/${jobId}`, { notesRich: value }),
        onSuccess: () => {
            invalidate();
            setEditingRich(false);
            toast.success("Office notes saved");
        },
        // Surface the server's message — see summary-card.tsx / Issue #715.
        onError: (e: any) => toast.error(e?.body?.message ?? e?.body?.error ?? "Couldn't save office notes"),
    });

    return (
        <div className="space-y-5 rounded-md border border-l-4 border-l-amber-500/70 p-3 dark:border-l-amber-400/70">
            {/* Add a note — the thread itself lives in the feed below */}
            <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-foreground/70">Add a note</span>
                    <span className="text-[10px] text-muted-foreground">
                        Chat with the tech. To tell him what to do, use “What the tech will do” at the top.
                    </span>
                </div>
                <div className="space-y-1.5">
                    <Textarea
                        rows={2}
                        value={draft}
                        placeholder="Add a note — everyone on this job sees it, nothing gets overwritten…"
                        onChange={(e) => setDraft(e.target.value)}
                        className="text-sm"
                    />
                    <div className="flex justify-end">
                        <Button
                            size="sm"
                            className="h-7 gap-1 text-xs"
                            disabled={addNote.isPending || !draft.trim()}
                            onClick={() => addNote.mutate(draft.trim())}
                        >
                            {addNote.isPending ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Send className="h-3 w-3" />
                            )}
                            Add note
                        </Button>
                    </div>
                </div>
            </div>

            {/* Office Notes — formatted Markdown, office-only */}
            <div className="space-y-2 border-t pt-4">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
                        Office Notes (formatted)
                    </span>
                    {!editingRich && (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 gap-1 text-xs"
                            onClick={() => {
                                setRichDraft(notesRich ?? "");
                                setEditingRich(true);
                            }}
                        >
                            <Pencil className="h-3 w-3" />
                            {notesRich ? "Edit" : "Add"}
                        </Button>
                    )}
                </div>
                {editingRich ? (
                    <div className="space-y-2" data-color-mode={colorMode}>
                        <Suspense
                            fallback={
                                <div className="flex items-center gap-2 text-xs text-foreground/70">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading editor…
                                </div>
                            }
                        >
                            <MDEditor
                                value={richDraft}
                                onChange={(v) => setRichDraft(v ?? "")}
                                height={220}
                                preview="edit"
                            />
                        </Suspense>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                onClick={() => saveRich.mutate(richDraft.trim() ? richDraft.trim() : null)}
                                disabled={saveRich.isPending}
                            >
                                {saveRich.isPending ? "Saving…" : "Save"}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingRich(false)}
                                disabled={saveRich.isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : notesRich ? (
                    <div data-color-mode={colorMode}>
                        <Suspense fallback={<p className="text-sm whitespace-pre-wrap">{notesRich}</p>}>
                            <MDMarkdown
                                source={notesRich}
                                style={{ background: "transparent", fontSize: "0.875rem" }}
                            />
                        </Suspense>
                    </div>
                ) : (
                    <p className="text-xs text-foreground/80">
                        Nothing yet. Add formatted notes for the office — headings, bold, lists, and more.
                    </p>
                )}
            </div>
        </div>
    );
}
